import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Users, Briefcase, TrendingUp, AlertTriangle, BarChart3,
  Clock, CheckCircle2, Loader2, ArrowLeft, Activity,
  Zap, Bell, Eye, Share2, Copy, MessageCircle, Crown,
  DollarSign, Building2, Gift, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const AdminAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [quickActions, setQuickActions] = useState(null);
  const [trends, setTrends] = useState(null);
  const [postActivation, setPostActivation] = useState(null);
  const [notifications, setNotifications] = useState(null);
  const [trendPeriod, setTrendPeriod] = useState(30);

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    fetchTrends();
  }, [trendPeriod]);

  const fetchAll = async () => {
    try {
      const [ovRes, qaRes, paRes, notifRes] = await Promise.all([
        api.get('/admin/analytics/overview'),
        api.get('/admin/analytics/quick-actions'),
        api.get('/admin/analytics/post-activation'),
        api.get('/admin/analytics/notifications'),
      ]);
      setOverview(ovRes.data);
      setQuickActions(qaRes.data);
      setPostActivation(paRes.data);
      setNotifications(notifRes.data);
    } catch {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrends = async () => {
    try {
      const res = await api.get(`/admin/analytics/behavior-trends?days=${trendPeriod}`);
      setTrends(res.data);
    } catch {}
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  const ov = overview?.overview || {};
  const health = overview?.staffing_health || {};

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-analytics">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/home')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <button onClick={() => navigate('/admin/home')} className="text-sm font-bold text-[#03DFFC] hover:opacity-80 hidden sm:inline">iEvntür</button>
            <h1 className="text-lg font-semibold text-gray-900">Platform Analytics</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/revenue')} data-testid="go-to-revenue">
              <DollarSign className="w-4 h-4 mr-1.5" />Revenue
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/referral-leaderboard')}>
              <Crown className="w-4 h-4 mr-1.5" />Referral Leaderboard
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Section 1: Platform Overview */}
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Platform Overview</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="platform-overview">
            <Card>
              <CardContent className="p-4 text-center">
                <Users className="w-5 h-5 text-indigo-500 mx-auto mb-1" />
                <p className="text-2xl font-bold text-gray-900">{ov.total_users}</p>
                <p className="text-[11px] text-gray-500">Total Users</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Activity className="w-5 h-5 text-cyan-500 mx-auto mb-1" />
                <p className="text-2xl font-bold text-cyan-700">{ov.active_talent_7d}</p>
                <p className="text-[11px] text-gray-500">Active Talent (7d)</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Building2 className="w-5 h-5 text-violet-500 mx-auto mb-1" />
                <p className="text-2xl font-bold text-violet-700">{ov.active_clients_7d}</p>
                <p className="text-[11px] text-gray-500">Active Clients (7d)</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Briefcase className="w-5 h-5 text-teal-500 mx-auto mb-1" />
                <p className="text-2xl font-bold text-teal-700">{ov.total_bookings}</p>
                <p className="text-[11px] text-gray-500">Total Bookings</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
                <p className="text-2xl font-bold text-emerald-700">{ov.completed_bookings}</p>
                <p className="text-[11px] text-gray-500">Completed</p>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4 text-center">
                <TrendingUp className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-amber-700">{ov.fill_rate_pct}%</p>
                <p className="text-[11px] text-amber-600 font-medium">Fill Rate</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Section 2: Staffing Health */}
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Staffing Health</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="staffing-health">
            <Card>
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-gray-900">{health.open_shifts}</p>
                  <p className="text-sm text-gray-500">Open Shifts</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-amber-200">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-amber-700">{health.understaffed}</p>
                  <p className="text-sm text-gray-500">Understaffed</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-red-700">{health.urgent_shifts}</p>
                  <p className="text-sm text-red-600 font-medium">Urgent (&lt;3d, &lt;50% filled)</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Section 3: Quick Actions + Behavior Trends side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Quick Actions Usage */}
          <Card data-testid="quick-actions-analytics">
            <CardHeader className="py-3 px-5">
              <CardTitle className="text-sm font-semibold">Quick Actions Usage (30d)</CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-5">
              <div className="space-y-2">
                {(quickActions?.top_actions || []).map((a, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded border">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-400 w-5">#{i+1}</span>
                      <span className="text-sm font-medium text-gray-800">{a.action_id}</span>
                      <Badge className="text-[9px] bg-gray-100 text-gray-600">{a.role}</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-indigo-600">{a.clicks}</span>
                      {i < 3 && <Badge className="text-[9px] bg-green-100 text-green-700">High Value</Badge>}
                    </div>
                  </div>
                ))}
              </div>
              {(quickActions?.least_actions || []).length > 0 && (
                <div className="mt-4 pt-3 border-t">
                  <p className="text-xs text-gray-500 mb-2">Least Used (flag for removal)</p>
                  {(quickActions?.least_actions || []).map((a, i) => (
                    <div key={i} className="flex items-center justify-between py-1">
                      <span className="text-xs text-gray-500">{a.action_id} ({a.role})</span>
                      <span className="text-xs text-red-500">{a.clicks} clicks</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3 pt-2 border-t text-center">
                <span className="text-xs text-gray-400">Reorder events: <strong>{quickActions?.reorder_events_30d || 0}</strong></span>
              </div>
            </CardContent>
          </Card>

          {/* Behavior Trends */}
          <Card data-testid="behavior-trends">
            <CardHeader className="py-3 px-5 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold">Behavior Trends</CardTitle>
              <div className="flex gap-1">
                <Button variant={trendPeriod === 7 ? 'default' : 'ghost'} size="sm" className="text-xs h-7" onClick={() => setTrendPeriod(7)}>7d</Button>
                <Button variant={trendPeriod === 30 ? 'default' : 'ghost'} size="sm" className="text-xs h-7" onClick={() => setTrendPeriod(30)}>30d</Button>
              </div>
            </CardHeader>
            <CardContent className="px-5 pb-5">
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500">Bookings</span>
                    <span className="text-xs font-bold text-teal-600">{(trends?.bookings || []).reduce((s,d) => s + d.count, 0)} total</span>
                  </div>
                  <div className="h-8 flex items-end gap-px">
                    {(trends?.bookings || []).slice(-14).map((d, i) => (
                      <div key={i} className="flex-1 bg-teal-400 rounded-t" style={{height: `${Math.max(4, (d.count / Math.max(...(trends?.bookings || [{count:1}]).map(x=>x.count), 1)) * 100)}%`}} title={`${d.date}: ${d.count}`} />
                    ))}
                    {(trends?.bookings || []).length === 0 && <p className="text-[10px] text-gray-400">No data</p>}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500">Logins</span>
                    <span className="text-xs font-bold text-indigo-600">{(trends?.logins || []).reduce((s,d) => s + d.count, 0)} total</span>
                  </div>
                  <div className="h-8 flex items-end gap-px">
                    {(trends?.logins || []).slice(-14).map((d, i) => (
                      <div key={i} className="flex-1 bg-indigo-400 rounded-t" style={{height: `${Math.max(4, (d.count / Math.max(...(trends?.logins || [{count:1}]).map(x=>x.count), 1)) * 100)}%`}} title={`${d.date}: ${d.count}`} />
                    ))}
                    {(trends?.logins || []).length === 0 && <p className="text-[10px] text-gray-400">No data</p>}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500">Quick Action Clicks</span>
                    <span className="text-xs font-bold text-pink-600">{(trends?.quick_actions || []).reduce((s,d) => s + d.count, 0)} total</span>
                  </div>
                  <div className="h-8 flex items-end gap-px">
                    {(trends?.quick_actions || []).slice(-14).map((d, i) => (
                      <div key={i} className="flex-1 bg-pink-400 rounded-t" style={{height: `${Math.max(4, (d.count / Math.max(...(trends?.quick_actions || [{count:1}]).map(x=>x.count), 1)) * 100)}%`}} title={`${d.date}: ${d.count}`} />
                    ))}
                    {(trends?.quick_actions || []).length === 0 && <p className="text-[10px] text-gray-400">No data</p>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Section 4: Post Activation Funnel + Notification Insights */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Post Activation Funnel */}
          <Card data-testid="post-activation-funnel">
            <CardHeader className="py-3 px-5">
              <CardTitle className="text-sm font-semibold">Post Activation Funnel (30d)</CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-5">
              {postActivation?.funnel && (
                <>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Copy className="w-4 h-4 text-cyan-500" />
                        <span className="text-sm text-gray-700">Copy Caption</span>
                      </div>
                      <span className="font-bold text-cyan-700">{postActivation.funnel.copy_caption}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Share2 className="w-4 h-4 text-blue-500" />
                        <span className="text-sm text-gray-700">Share Click</span>
                      </div>
                      <span className="font-bold text-blue-700">{postActivation.funnel.share_click}</span>
                    </div>
                    <div className="h-px bg-gray-200 my-1" />
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Triggered</span>
                      <span className="font-bold text-gray-900">{postActivation.funnel.triggered}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded h-2">
                      <div className="bg-cyan-500 rounded h-2" style={{width: `${postActivation.rates.engagement_rate}%`}} />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Engaged (responded)</span>
                      <span className="font-bold text-amber-700">{postActivation.funnel.engaged}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded h-2">
                      <div className="bg-green-500 rounded h-2" style={{width: `${postActivation.rates.post_rate}%`}} />
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span className="text-sm font-medium text-green-700">Posted</span>
                      </div>
                      <span className="font-bold text-green-700">{postActivation.funnel.posted}</span>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-lg font-bold text-cyan-600">{postActivation.rates.engagement_rate}%</p>
                      <p className="text-[10px] text-gray-500">Engagement</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-green-600">{postActivation.rates.post_rate}%</p>
                      <p className="text-[10px] text-gray-500">Post Rate</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-indigo-600">{postActivation.rates.overall_conversion}%</p>
                      <p className="text-[10px] text-gray-500">Conversion</p>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Notification Insights */}
          <Card data-testid="notification-insights">
            <CardHeader className="py-3 px-5">
              <CardTitle className="text-sm font-semibold">Notification Insights (30d)</CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-5">
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 border rounded-lg">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                    <Bell className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{notifications?.urgent_sent_30d || 0}</p>
                    <p className="text-sm text-gray-500">Urgent Notifications Sent</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 border rounded-lg">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-700">{notifications?.bookings_from_notifications || 0}</p>
                    <p className="text-sm text-gray-500">Resulted in Bookings</p>
                  </div>
                </div>
                <div className="p-4 border rounded-lg bg-gray-50 text-center">
                  <p className="text-3xl font-bold text-indigo-600">{notifications?.conversion_rate || 0}%</p>
                  <p className="text-sm text-gray-500">Notification → Booking Conversion</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
